import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {FiltersService} from '../../filters.service';
import {Filter} from '../filter-item/filter';
import {debounceTime, distinctUntilChanged} from 'rxjs/internal/operators';
import {FormControl} from '@angular/forms';
import {SearchService} from '../../../../search.service';
import {ActivatedRoute} from '@angular/router';
import {FilterBlock} from '../filter-block';
import {SortService} from '../../../../../shared/sort.service';
import {FilterSort} from '../../../../../config/filters/filter-sort.enum';
import {TranslateService} from '../../../../../shared/translate.service';
import {VirtualScrollerComponent} from 'ngx-virtual-scroller';
import {MessageService} from '../../../../../messages/message.service';
import {AppConfigService} from '../../../../../config/app-config.service';
import {ListService} from '../../../../../shared/list/list.service';

@Component({
  selector: 'app-filter-modal',
  templateUrl: './filter-modal.component.html',
  styleUrls: ['./filter-modal.component.css']
})
export class FilterModalComponent implements OnInit {

  @ViewChild(VirtualScrollerComponent)
  private virtualScroller: VirtualScrollerComponent;

  @Input() filterBlock: FilterBlock;

  materialization = false;

  quickField = new FormControl();

  filtersCopy: Filter[] = [];

  allOr = false;

  allAnd = false;

  allExclude = false;

  windowHeader = '';

  filtersString: string = '';

  private filtersCopyBase: Filter[] = [];

  private modifiedFilters: Filter[] = [];

  private filtersLength = 0;

  private skipFilters: Filter[] = [];

  constructor(public activeModal: NgbActiveModal,
              public translateService: TranslateService,
              public filtersService: FiltersService,
              protected searchService: SearchService,
              protected route: ActivatedRoute,
              protected sortService: SortService,
              protected messageService: MessageService,
              protected appConfigService: AppConfigService,
              protected listService: ListService) {
  }

  protected getFilterService() {
    return this.filtersService;
  }

  focusOnAnItem(item: Filter) {
    const focusedIndex = this.filtersCopy.indexOf(item);
    if (focusedIndex > 5) {
      this.virtualScroller.scrollInto(item);
    }
  }

  ngOnInit(): void {
    this.filtersString = this.getFilterService().filtersToString();
    this.skipFilters = this.getFilterService().hideFilters();
    this.materialization = this.isMaterializationFilter();
    this.filtersLength = (this.filtersString + '').length;
    this.filterBlock.filters.forEach(filter => {
      let copy = new Filter(filter);
      this.filtersCopy.push(copy);
      this.filtersCopyBase.push(copy);
    });
    let queryParamMap = this.route.snapshot.queryParamMap;
    this.searchService.findFilters('', '', this.filterBlock.filterConfig.name, this.filtersString, this.listService.matIds.value, this.getFilterService().getAggregates())
      .subscribe((data: any[]) => {
        this.recreateFiltersList(data);
        this.filtersCopyBase = this.filtersCopy;
      });

    this.quickField.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(filterPhrase => {
      if (filterPhrase != '' && filterPhrase.trim().length > 2) {
        let phrase = queryParamMap.get('query');
        this.searchService.findFilters(phrase, filterPhrase, this.filterBlock.filterConfig.name, this.filtersString, this.listService.matIds.value, this.getFilterService().getAggregates())
          .subscribe(data => {
            this.recreateFiltersList(data);
          });
      } else {
        this.filtersCopy = this.filtersCopyBase;
        this.applyModifiedFiltersToReceived();
        this.windowHeader = this.translateService.translate(this.filterBlock.filterConfig.translation) + ' (' + this.filtersCopy.length + ')';
        if (this.allOr === true && this.filtersCopy.find(fc => !fc.or) !== undefined) {
          this.allOr = false;
        }
        if (this.allAnd === true && this.filtersCopy.find(fc => !this.isAllSelected(fc)) !== undefined) {
          this.allAnd = false;
        }
        if (this.allExclude === true && this.filtersCopy.find(fc => !fc.excluded) !== undefined) {
          this.allExclude = false;
        }
      }

    });
    this.windowHeader = this.translateService.translate(this.filterBlock.filterConfig.translation) + ' (' + this.filtersCopy.length + ')';
  }

  recreateFiltersList(data: any[]) {
    this.filtersCopy = [];
    this.applyModifiedFiltersToReceived();
    let freshFilters = data.map(item => new Filter(item));
    freshFilters.forEach(freshFilter => this.filtersCopy.push(freshFilter));
    this.windowHeader = this.translateService.translate(this.filterBlock.filterConfig.translation) + ' (' + this.filtersCopy.length + ')';
    this.filtersCopyBase.forEach(filterCopy => this.appendOrUpdateFilter(filterCopy, freshFilters, false));
    this.filtersCopy.sort((filterA, filterB) => this.pickSortFunction(filterA, filterB));
    this.skipFilters.forEach(oldFilter => this.appendOrUpdateFilter(oldFilter, this.filtersCopy, false));
    this.filtersCopy = this.filtersCopy.filter(filter => !filter.hideInResult);
  }

  applyFilters() {
    this.modifiedFilters.forEach(filterCopy => this.appendOrUpdateFilter(filterCopy, this.getFilterService().allRegularFacets.value));
    this.getFilterService().navigateByFilters();
    this.activeModal.close();
  }

  appendOrUpdateFilter(filterCopy: Filter, filters: Filter[], addToGlobal: boolean = true) {
    const baseResponseFilter = this.findMatchingFilter(filterCopy, filters);
    if (baseResponseFilter) {
      baseResponseFilter.selected = filterCopy.selected;
      baseResponseFilter.or = filterCopy.or;
      baseResponseFilter.excluded = filterCopy.excluded;
      baseResponseFilter.hideInResult = filterCopy.hideInResult;
    } else if (addToGlobal) {
      this.getFilterService().appendFilter(filterCopy);
    }
  }

  toggleOr(checked: boolean, filter: Filter) {
    const result = this.toggle(filter, checked, true, false);
    this.allAnd = false;
    this.allExclude = false;
    return result;
  }

  toggleAnd(checked: boolean, filter: Filter) {
    const result = this.toggle(filter, checked, false, false);
    this.allExclude = false;
    this.allOr = false;
    return result;
  }

  toggleEx(checked: boolean, filter: Filter) {
    const result = this.toggle(filter, checked, false, true);
    this.allOr = false;
    this.allAnd = false;
    return result;
  }

  private applyModifiedFiltersToReceived() {
    this.modifiedFilters.forEach(filterCopy => this.appendOrUpdateFilter(filterCopy, this.filtersCopy, false));
  }

  private findMatchingFilter(filterToFind: Filter, filters: Filter[]): Filter {
    return filters.find(filterCopy => filterCopy.field == filterToFind.field && filterCopy.value == filterToFind.value);
  }

  private toggle(filter: Filter, selected: boolean, or: boolean, excluded: boolean) {
    if (!selected || this.canToggle(filter)) {
      filter.selected = selected;
      if (selected) {
        filter.or = or;
        filter.excluded = excluded;
      } else {
        filter.or = false;
        filter.excluded = false;
      }
      if (!this.modifiedFilters.includes(filter)) {
        this.modifiedFilters.push(filter);
      }

      this.updateToggleAll(selected, or, excluded);
      return true;
    } else {
      this.messageService.errorMessage('Filters.urlError.label', 'Filters.urlError.info');
      return false;
    }
  }

  private updateToggleAll(selected: boolean, or: boolean, excluded: boolean) {
    if (selected) {
      if (or) {
        if (this.allOr !== selected && this.filtersCopy.find(fc => !fc.or) === undefined) {
          this.allOr = selected;
        }
      } else if (excluded) {
        if (this.allExclude !== selected && this.filtersCopy.find(fc => !fc.excluded) === undefined) {
          this.allExclude = selected;
        }
      } else {
        if (this.allAnd !== selected && this.filtersCopy.find(fc => !this.isAllSelected(fc)) === undefined) {
          this.allAnd = selected;
        }
      }
    } else {
      if (or) {
        if (this.allOr !== selected) {
          this.allOr = selected;
        }
      } else if (excluded) {
        if (this.allExclude !== selected) {
          this.allExclude = selected;
        }
      } else {
        if (this.allAnd !== selected) {
          this.allAnd = selected;
        }
      }
    }
  }

  private isAllSelected(filter: Filter) {
    return filter.or || filter.excluded;
  }

  private pickSortFunction(filterA: Filter, filterB: Filter) {
    if (filterA.selected === filterB.selected) {
      switch (this.filterBlock.filterConfig.sort) {
        case FilterSort.ALPHABETICAL:
          return this.sortService.sortSelectedThenAlphaFirst(filterA, filterB);
        default:
          return this.sortService.sortSelectedThenFreqFirst(filterA, filterB);
      }
    } else {
      return filterA.selected ? -1 : 1;
    }
  }

  private canToggle(filter: Filter) {
    const modalFiltersLength = this.filtersCopy
      .filter(item => item.selected && item != filter)
      .map(item => item.getUrlLength())
      .reduce((prev, curr) => prev + curr, this.filtersLength);
    const otherFiltersLength = this.getFilterService().calculateUrlLengthWithFilters('');
    const currentFilterLength = filter.getUrlLength();
    return modalFiltersLength + otherFiltersLength + currentFilterLength + 50 < this.appConfigService.getConfig().maxUrlLength;
  }

  private isMaterializationFilter() {
    const filters = this.getFilterService().allRegularFacets.value.filter(filter => filter.field == 'type');
    return filters.length == 1 && filters[0].value == 'materialization';
  }
}
